import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  Truck,
  CheckCircle
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import goodsReceiptNoteService from '../../services/goodsReceiptNoteService'
import purchaseOrderService from '../../services/purchaseOrderService'
import { getUsers } from '../../services/projectService'
import '../../styles/expenseClaimForm.css'

const GoodsReceiptNoteForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = location.pathname.includes('/edit')
  const isView = location.pathname.includes('/view')
  const grnData = location.state?.grnData

  const [formData, setFormData] = useState({
    grnNumber: '',
    purchaseOrder: '',
    purchaseOrderNumber: '',
    supplier: '',
    supplierName: '',
    supplierCode: '',
    receiptDate: new Date().toISOString().split('T')[0],
    receivedBy: '',
    receivedByName: '',
    receiptItems: [],
    deliveryInformation: {
      deliveryNoteNumber: '',
      transporterName: '',
      vehicleNumber: '',
      driverName: '',
      driverContact: '',
      deliveryDate: ''
    },
    qualityInspection: {
      inspectedBy: '',
      inspectedByName: '',
      inspectionDate: '',
      overallQualityStatus: 'Pending',
      inspectionNotes: ''
    },
    warehouse: '',
    storageLocation: '',
    notes: ''
  })

  const [isLoadingGRNNumber, setIsLoadingGRNNumber] = useState(true)
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)

  // Load initial data
  useEffect(() => {
    if (!isEdit && !isView) {
      fetchGRNNumber()
    } else if (grnData) {
      loadGRNData(grnData._id)
    }

    // Load dropdown data
    loadDropdownData()
  }, [])

  const fetchGRNNumber = async () => {
    try {
      setIsLoadingGRNNumber(true)
      const response = await goodsReceiptNoteService.getNextGRNNumber()
      if (response.success && response.data) {
        setFormData(prev => ({
          ...prev,
          grnNumber: response.data.grnNumber || response.data.formattedValue || 'GRN0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching GRN number:', error)
      setFormData(prev => ({ ...prev, grnNumber: 'GRN0001' }))
    } finally {
      setIsLoadingGRNNumber(false)
    }
  }

  const loadGRNData = async (id) => {
    try {
      setLoading(true)
      const response = await goodsReceiptNoteService.getGRNById(id)
      if (response.success && response.data) {
        const grn = response.data
        setFormData({
          grnNumber: grn.grnNumber || '',
          purchaseOrder: grn.purchaseOrder?._id || grn.purchaseOrder || '',
          purchaseOrderNumber: grn.purchaseOrderNumber || '',
          supplier: grn.supplier?._id || grn.supplier || '',
          supplierName: grn.supplierName || '',
          supplierCode: grn.supplierCode || '',
          receiptDate: grn.receiptDate ? new Date(grn.receiptDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          receivedBy: grn.receivedBy?._id || grn.receivedBy || '',
          receivedByName: grn.receivedByName || '',
          receiptItems: grn.receiptItems || [],
          deliveryInformation: grn.deliveryInformation || {
            deliveryNoteNumber: '',
            transporterName: '',
            vehicleNumber: '',
            driverName: '',
            driverContact: '',
            deliveryDate: ''
          },
          qualityInspection: grn.qualityInspection || {
            inspectedBy: '',
            inspectedByName: '',
            inspectionDate: '',
            overallQualityStatus: 'Pending',
            inspectionNotes: ''
          },
          warehouse: grn.warehouse || '',
          storageLocation: grn.storageLocation || '',
          notes: grn.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading GRN data:', error)
      toast.error('Failed to load GRN data')
    } finally {
      setLoading(false)
      setIsLoadingGRNNumber(false)
    }
  }

  const loadDropdownData = async () => {
    try {
      const [posRes, usersRes] = await Promise.all([
        purchaseOrderService.getAllPurchaseOrders({ status: 'Sent', limit: 1000 }),
        getUsers()
      ])

      if (posRes.success) setPurchaseOrders(posRes.data || [])
      if (usersRes.success) setUsers(usersRes.data || [])
    } catch (error) {
      console.error('Error loading dropdown data:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('deliveryInformation.')) {
      const deliveryField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        deliveryInformation: {
          ...prev.deliveryInformation,
          [deliveryField]: value
        }
      }))
    } else if (name.startsWith('qualityInspection.')) {
      const qualityField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        qualityInspection: {
          ...prev.qualityInspection,
          [qualityField]: value
        }
      }))
    } else if (name === 'purchaseOrder') {
      loadPurchaseOrderData(value)
    } else if (name === 'receivedBy') {
      const selectedUser = users.find(u => u._id === value)
      setFormData(prev => ({
        ...prev,
        receivedBy: value,
        receivedByName: selectedUser ? `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() : ''
      }))
    } else if (name === 'qualityInspection.inspectedBy') {
      const selectedUser = users.find(u => u._id === value)
      setFormData(prev => ({
        ...prev,
        qualityInspection: {
          ...prev.qualityInspection,
          inspectedBy: value,
          inspectedByName: selectedUser ? `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() : ''
        }
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const loadPurchaseOrderData = async (purchaseOrderId) => {
    if (!purchaseOrderId) return

    try {
      const response = await goodsReceiptNoteService.getPurchaseOrderForGRN(purchaseOrderId)
      if (response.success && response.data) {
        const po = response.data
        setFormData(prev => ({
          ...prev,
          purchaseOrder: purchaseOrderId,
          purchaseOrderNumber: po.purchaseOrderNumber || '',
          supplier: po.supplier?._id || po.supplier || '',
          supplierName: po.supplierName || '',
          supplierCode: po.supplierCode || '',
          receiptItems: po.orderItems?.map((item, idx) => ({
            itemNumber: idx + 1,
            itemType: item.itemType || 'Product',
            product: item.product?._id || item.product || '',
            productName: item.productName || '',
            productCode: item.productCode || '',
            service: item.service?._id || item.service || '',
            serviceName: item.serviceName || '',
            serviceCode: item.serviceCode || '',
            category: item.category?._id || item.category || '',
            categoryName: item.categoryName || '',
            description: item.description || '',
            orderedQuantity: item.quantity || 0,
            receivedQuantity: 0,
            rejectedQuantity: 0,
            rejectedReason: '',
            acceptedQuantity: 0,
            unitOfMeasurement: item.unitOfMeasurement || 'Unit',
            unitPrice: item.unitPrice || 0,
            totalAmount: 0,
            currency: item.currency || 'INR',
            qualityStatus: 'Pending',
            qualityCheckNotes: '',
            batchNumber: '',
            lotNumber: '',
            expiryDate: '',
            serialNumbers: []
          })) || []
        }))
      }
    } catch (error) {
      console.error('Error loading purchase order data:', error)
      toast.error('Failed to load purchase order data')
    }
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.receiptItems]
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === 'receivedQuantity' || field === 'rejectedQuantity' || field === 'unitPrice'
        ? parseFloat(value) || 0
        : value
    }

    // Calculate accepted quantity
    const receivedQty = updatedItems[index].receivedQuantity || 0
    const rejectedQty = updatedItems[index].rejectedQuantity || 0
    updatedItems[index].acceptedQuantity = Math.max(0, receivedQty - rejectedQty)

    // Calculate total amount based on accepted quantity
    const acceptedQty = updatedItems[index].acceptedQuantity
    const unitPrice = updatedItems[index].unitPrice || 0
    updatedItems[index].totalAmount = acceptedQty * unitPrice

    setFormData(prev => ({
      ...prev,
      receiptItems: updatedItems
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.purchaseOrder || !formData.supplier || !formData.receivedBy) {
        toast.error('Please fill in all required fields (Purchase Order, Supplier, and Received By)')
        return
      }

      if (!formData.receiptItems || formData.receiptItems.length === 0) {
        toast.error('Please add at least one receipt item')
        return
      }

      setLoading(true)

      if (isEdit && grnData?._id) {
        const response = await goodsReceiptNoteService.updateGRN(grnData._id, formData)
        if (response.success) {
          toast.success('GRN updated successfully!')
          navigate('/procurement/goods-receipt-note')
        } else {
          toast.error(response.message || 'Failed to update GRN')
        }
      } else {
        const response = await goodsReceiptNoteService.createGRN(formData)
        if (response.success) {
          toast.success('GRN created successfully!')
          navigate('/procurement/goods-receipt-note')
        } else {
          toast.error(response.message || 'Failed to create GRN')
        }
      }
    } catch (error) {
      console.error('Error saving GRN:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save GRN')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async () => {
    if (!grnData?._id) {
      toast.error('Please save the GRN first')
      return
    }

    try {
      setLoading(true)
      const response = await goodsReceiptNoteService.acceptGRN(grnData._id)
      if (response.success) {
        toast.success('GRN accepted successfully!')
        navigate('/procurement/goods-receipt-note')
      } else {
        toast.error(response.message || 'Failed to accept GRN')
      }
    } catch (error) {
      console.error('Error accepting GRN:', error)
      if (!error._errorHandled) {
        toast.error('Failed to accept GRN')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (isEdit || isView) {
      loadGRNData(grnData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        purchaseOrder: '',
        purchaseOrderNumber: '',
        supplier: '',
        supplierName: '',
        supplierCode: '',
        receivedBy: '',
        receivedByName: '',
        receiptItems: [],
        deliveryInformation: {
          deliveryNoteNumber: '',
          transporterName: '',
          vehicleNumber: '',
          driverName: '',
          driverContact: '',
          deliveryDate: ''
        },
        qualityInspection: {
          inspectedBy: '',
          inspectedByName: '',
          inspectionDate: '',
          overallQualityStatus: 'Pending',
          inspectionNotes: ''
        },
        warehouse: '',
        storageLocation: '',
        notes: ''
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Procurement', path: '/procurement' },
    { label: 'Receipts', path: '/procurement/goods-receipt-note' },
    { 
      label: isEdit ? 'Edit GRN' : isView ? 'View GRN' : 'New GRN', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (loading && (isEdit || isView)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading GRN...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEdit ? 'Edit GRN' : isView ? 'View GRN' : 'New GRN'}</h1>
          <p>{isEdit ? 'Update goods receipt note' : isView ? 'View goods receipt note details' : 'Create a new goods receipt note'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/procurement/goods-receipt-note')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Receipt Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: GRN Number, Purchase Order, Supplier, Receipt Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="grnNumber" className="form-label required">
                GRN Number
              </label>
              <input
                type="text"
                id="grnNumber"
                name="grnNumber"
                value={isLoadingGRNNumber ? 'Generating...' : formData.grnNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="purchaseOrder" className="form-label required">
                Purchase Order
              </label>
              <select
                id="purchaseOrder"
                name="purchaseOrder"
                value={formData.purchaseOrder}
                onChange={handleInputChange}
                disabled={isView || isEdit}
                className="form-input"
                required
              >
                <option value="">Select Purchase Order</option>
                {purchaseOrders.map(po => (
                  <option key={po._id} value={po._id}>
                    {po.purchaseOrderNumber} - {po.supplierName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="supplier" className="form-label required">
                Supplier
              </label>
              <input
                type="text"
                id="supplier"
                name="supplierName"
                value={formData.supplierName}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="receiptDate" className="form-label required">
                Receipt Date
              </label>
              <input
                type="date"
                id="receiptDate"
                name="receiptDate"
                value={formData.receiptDate}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                required
              />
            </div>
          </div>

          {/* Row 2: Received By */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="receivedBy" className="form-label required">
                Received By
              </label>
              <select
                id="receivedBy"
                name="receivedBy"
                value={formData.receivedBy}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                required
              >
                <option value="">Select User</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Receipt Items Section */}
          <div className="form-section-divider">
            <div className="section-header-with-action">
              <div>
                <h2 className="section-title">Receipt Items</h2>
                <p className="section-subtitle">Record received quantities for each item</p>
              </div>
            </div>
          </div>

          {formData.receiptItems.map((item, index) => (
            <div key={index} className="expense-item-card-modern">
              <div className="expense-item-header">
                <div className="expense-item-number">
                  <span className="item-number-badge">{index + 1}</span>
                  <span className="item-number-text">Item {index + 1}: {item.productName || item.serviceName || 'N/A'}</span>
                </div>
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label className="form-label">Ordered Quantity</label>
                  <input
                    type="number"
                    value={item.orderedQuantity || 0}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Received Quantity</label>
                  <input
                    type="number"
                    value={item.receivedQuantity || 0}
                    onChange={(e) => handleItemChange(index, 'receivedQuantity', e.target.value)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    required
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Rejected Quantity</label>
                  <input
                    type="number"
                    value={item.rejectedQuantity || 0}
                    onChange={(e) => handleItemChange(index, 'rejectedQuantity', e.target.value)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Accepted Quantity</label>
                  <input
                    type="number"
                    value={item.acceptedQuantity || 0}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', fontWeight: '600' }}
                  />
                </div>
              </div>

              <div className="grid-row-4" style={{ marginTop: '10px' }}>
                <div className="form-group">
                  <label className="form-label">Unit</label>
                  <input
                    type="text"
                    value={item.unitOfMeasurement || ''}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Unit Price</label>
                  <input
                    type="number"
                    value={item.unitPrice || 0}
                    onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Total Amount</label>
                  <div className="expense-item-total-display">
                    {item.currency || 'INR'} {(item.totalAmount || 0).toFixed(2)}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Quality Status</label>
                  <select
                    value={item.qualityStatus || 'Pending'}
                    onChange={(e) => handleItemChange(index, 'qualityStatus', e.target.value)}
                    className="form-input"
                    disabled={isView}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Passed">Passed</option>
                    <option value="Failed">Failed</option>
                    <option value="Partial">Partial</option>
                  </select>
                </div>
              </div>

              <div className="grid-row-4" style={{ marginTop: '10px' }}>
                <div className="form-group">
                  <label className="form-label">Rejection Reason</label>
                  <input
                    type="text"
                    value={item.rejectedReason || ''}
                    onChange={(e) => handleItemChange(index, 'rejectedReason', e.target.value)}
                    className="form-input"
                    placeholder="Reason for rejection"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Quality Check Notes</label>
                  <input
                    type="text"
                    value={item.qualityCheckNotes || ''}
                    onChange={(e) => handleItemChange(index, 'qualityCheckNotes', e.target.value)}
                    className="form-input"
                    placeholder="Quality inspection notes"
                    disabled={isView}
                  />
                </div>

                {item.itemType === 'Product' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Batch Number</label>
                      <input
                        type="text"
                        value={item.batchNumber || ''}
                        onChange={(e) => handleItemChange(index, 'batchNumber', e.target.value)}
                        className="form-input"
                        disabled={isView}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        value={item.expiryDate ? new Date(item.expiryDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                        className="form-input"
                        disabled={isView}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}

          {formData.receiptItems.length === 0 && !isView && (
            <div className="empty-state-card">
              <div className="empty-state-icon">
                <Truck size={48} />
              </div>
              <h3 className="empty-state-title">No receipt items</h3>
              <p className="empty-state-description">Select a Purchase Order to auto-fill items</p>
            </div>
          )}

          {/* Delivery Information */}
          <div className="section-header"><h3>Delivery Information</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="deliveryInformation.deliveryNoteNumber" className="form-label">
                Delivery Note Number
              </label>
              <input
                type="text"
                id="deliveryInformation.deliveryNoteNumber"
                name="deliveryInformation.deliveryNoteNumber"
                value={formData.deliveryInformation.deliveryNoteNumber}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Delivery note number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="deliveryInformation.transporterName" className="form-label">
                Transporter Name
              </label>
              <input
                type="text"
                id="deliveryInformation.transporterName"
                name="deliveryInformation.transporterName"
                value={formData.deliveryInformation.transporterName}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Transporter name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="deliveryInformation.vehicleNumber" className="form-label">
                Vehicle Number
              </label>
              <input
                type="text"
                id="deliveryInformation.vehicleNumber"
                name="deliveryInformation.vehicleNumber"
                value={formData.deliveryInformation.vehicleNumber}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Vehicle number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="deliveryInformation.driverName" className="form-label">
                Driver Name
              </label>
              <input
                type="text"
                id="deliveryInformation.driverName"
                name="deliveryInformation.driverName"
                value={formData.deliveryInformation.driverName}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Driver name"
              />
            </div>
          </div>

          {/* Quality Inspection */}
          <div className="section-header"><h3>Quality Inspection</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="qualityInspection.inspectedBy" className="form-label">
                Inspected By
              </label>
              <select
                id="qualityInspection.inspectedBy"
                name="qualityInspection.inspectedBy"
                value={formData.qualityInspection.inspectedBy}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
              >
                <option value="">Select Inspector</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="qualityInspection.inspectionDate" className="form-label">
                Inspection Date
              </label>
              <input
                type="date"
                id="qualityInspection.inspectionDate"
                name="qualityInspection.inspectionDate"
                value={formData.qualityInspection.inspectionDate ? new Date(formData.qualityInspection.inspectionDate).toISOString().split('T')[0] : ''}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="qualityInspection.overallQualityStatus" className="form-label">
                Overall Quality Status
              </label>
              <select
                id="qualityInspection.overallQualityStatus"
                name="qualityInspection.overallQualityStatus"
                value={formData.qualityInspection.overallQualityStatus}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
              >
                <option value="Pending">Pending</option>
                <option value="Passed">Passed</option>
                <option value="Failed">Failed</option>
                <option value="Partial">Partial</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="qualityInspection.inspectionNotes" className="form-label">
              Inspection Notes
            </label>
            <textarea
              id="qualityInspection.inspectionNotes"
              name="qualityInspection.inspectionNotes"
              value={formData.qualityInspection.inspectionNotes}
              onChange={handleInputChange}
              disabled={isView}
              className="form-input"
              rows="3"
              placeholder="Quality inspection notes..."
            />
          </div>

          {/* Warehouse Information */}
          <div className="section-header"><h3>Warehouse Information</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="warehouse" className="form-label">
                Warehouse
              </label>
              <input
                type="text"
                id="warehouse"
                name="warehouse"
                value={formData.warehouse}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Warehouse name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="storageLocation" className="form-label">
                Storage Location
              </label>
              <input
                type="text"
                id="storageLocation"
                name="storageLocation"
                value={formData.storageLocation}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Storage location"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isView}
              className="form-input"
              rows="3"
              placeholder="Enter any additional notes"
            />
          </div>

          {/* Action Buttons */}
          {!isView && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleReset}
                className="btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn-primary"
                disabled={loading}
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: loading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save'}
              </button>
              {isEdit && grnData && grnData.status !== 'Accepted' && grnData.status !== 'Rejected' && grnData.status !== 'Cancelled' && (
                <button
                  type="button"
                  onClick={handleAccept}
                  className="btn-primary"
                  disabled={loading}
                  style={{
                    padding: '10px 24px',
                    background: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  <CheckCircle size={16} />
                  Accept GRN
                </button>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default GoodsReceiptNoteForm

